// Iter 82.8 Sprint 2 / Phase E3.0 — Salary Structure Detail.
// Shows the structure header, the operator-mandated preview breakdown
// (Monthly · Annual · Gross · Employer · Total CTC) and an Amend
// dialog with SIDE-BY-SIDE comparison of old → new values BEFORE
// confirmation.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, ClockCounterClockwise, GitBranch, Calculator,
    CaretRight, Sparkle, X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { formatDate } from "../../lib/format";
import { buildComponentNameMap, prettyFormula, formulaTooltip } from "../../lib/formulaFormat";

const NATURE_TONE = {
    earning: "bg-emerald-100 text-emerald-800",
    deduction: "bg-rose-100 text-rose-800",
    employer_contribution: "bg-violet-100 text-violet-800",
    reimbursement: "bg-sky-100 text-sky-800",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function PayrollStructureDetail() {
    const { can } = useAuth();
    const canEdit = can("payroll:admin");
    const { sid } = useParams();
    const navigate = useNavigate();
    const [structure, setStructure] = useState(null);
    const [components, setComponents] = useState([]);
    const [preview, setPreview] = useState(null);
    const [history, setHistory] = useState(null);
    const [gross, setGross] = useState(60000);
    const [paidDays, setPaidDays] = useState(30);
    const [monthDays, setMonthDays] = useState(30);
    const [openAmend, setOpenAmend] = useState(false);
    const [amendForm, setAmendForm] = useState({
        effective_from: "", amend_reason: "", components: [],
    });
    const [amendPreview, setAmendPreview] = useState(null);

    const loadStructure = useCallback(async () => {
        try {
            const [s, h, comp] = await Promise.all([
                api.get(`/payroll/structure/${sid}`),
                api.get(`/payroll/structure/${sid}/history`),
                api.get("/payroll/component/list", { params: { is_active: true } }),
            ]);
            setStructure(s.data);
            setHistory(h.data);
            setComponents(comp.data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load structure"));
        }
    }, [sid]);

    const computePreview = useCallback(async () => {
        if (!structure) return;
        try {
            const { data } = await api.post(`/payroll/structure/${sid}/preview`, {
                gross_per_month: Number(gross),
                paid_days: Number(paidDays),
                month_days: Number(monthDays),
                working_days: Number(paidDays),
            });
            setPreview(data);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        }
    }, [sid, structure, gross, paidDays, monthDays]);

    useEffect(() => { loadStructure(); }, [loadStructure]);
    useEffect(() => { if (structure) computePreview(); }, [structure, computePreview]);

    const openAmendDialog = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setAmendForm({
            effective_from: tomorrow.toISOString().slice(0, 10),
            amend_reason: "",
            components: structure.components.map((c) => ({
                component_code: c.component_code,
                value_or_formula: c.value_or_formula,
                monthly_cap: c.monthly_cap,
                annual_cap: c.annual_cap,
                sequence_no: c.sequence_no,
            })),
        });
        setAmendPreview(null);
        setOpenAmend(true);
    };

    const previewAmend = async () => {
        // Use a transient structure model — compute preview locally is
        // complex; instead, simulate by calling the existing preview
        // endpoint with the SAME structure_id but using a temporary
        // "/preview-payload" not yet supported, so we compare via
        // post-create-amend flow. For now we render the diff using
        // local DSL evaluation against the components list.
        try {
            // Locally compute new monthly values by hitting validate-formula
            // for each component formula against a sample context.
            const ctx = { BASIC: 0, GROSS: Number(gross), CTC_ANNUAL: Number(gross) * 12,
                          WORKING_DAYS: Number(paidDays), PAID_DAYS: Number(paidDays),
                          LOP_DAYS: 0, MONTH_DAYS: Number(monthDays) };
            // First-pass: compute BASIC (sequence 10 typically), then iterate.
            const sorted = [...amendForm.components].sort((a, b) => (a.sequence_no || 999) - (b.sequence_no || 999));
            const rows = [];
            for (const row of sorted) {
                const { data } = await api.post("/payroll/component/validate-formula", {
                    formula: row.value_or_formula,
                    context: ctx,
                });
                const m = Number(data.sample_value);
                rows.push({ component_code: row.component_code, monthly: m, annual: m * 12 });
                ctx[row.component_code] = m;
            }
            setAmendPreview({ rows });
        } catch (err) {
            toast.error(formatApiError(err, "Cannot preview amendment"));
        }
    };

    const confirmAmend = async () => {
        try {
            const payload = {
                effective_from: amendForm.effective_from,
                amend_reason: amendForm.amend_reason,
                components: amendForm.components.map((c) => ({
                    component_code: c.component_code,
                    value_or_formula: c.value_or_formula,
                    monthly_cap: c.monthly_cap,
                    annual_cap: c.annual_cap,
                    sequence_no: c.sequence_no || 100,
                })),
            };
            const { data } = await api.post(`/payroll/structure/${sid}/amend`, payload);
            toast.success(`Amended → v${data.version_no}`);
            setOpenAmend(false);
            navigate(`/admin/payroll/structures/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Amend failed"));
        }
    };

    if (!structure) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="struct-detail-loading">
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="struct-detail-page">
            <PageHeader
                eyebrow={`Structure · v${structure.version_no}`}
                title={`${structure.code} · ${structure.name}`}
                description={`${structure.employee_band} · effective ${formatDate(structure.effective_from)}${structure.effective_to ? ` → ${formatDate(structure.effective_to)}` : " (active)"}`}
                actions={(
                    <div className="flex gap-2">
                        <Link to="/admin/payroll/structures">
                            <Button variant="outline" size="sm" data-testid="struct-detail-back">
                                <ArrowLeft size={14} className="mr-1" /> Back
                            </Button>
                        </Link>
                        {!structure.effective_to && canEdit && (
                            <Button onClick={openAmendDialog} size="sm" data-testid="struct-detail-amend-btn">
                                <GitBranch size={14} className="mr-1" /> Amend (new version)
                            </Button>
                        )}
                    </div>
                )}
            />

            {/* Preview controls */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="struct-detail-preview-controls">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="w-48">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Gross / Month (₹)</Label>
                        <Input type="number" value={gross} onChange={(e) => setGross(e.target.value)} data-testid="preview-gross" />
                    </div>
                    <div className="w-32">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Paid Days</Label>
                        <Input type="number" value={paidDays} onChange={(e) => setPaidDays(e.target.value)} data-testid="preview-paid" />
                    </div>
                    <div className="w-32">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Month Days</Label>
                        <Input type="number" value={monthDays} onChange={(e) => setMonthDays(e.target.value)} data-testid="preview-month" />
                    </div>
                    <Button size="sm" onClick={computePreview} data-testid="preview-recompute">
                        <Calculator size={14} className="mr-1" /> Recompute
                    </Button>
                </div>
            </section>

            {/* Totals */}
            {preview && (
                <section className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="preview-totals">
                    <Total label="Gross Monthly" value={preview.totals.gross_monthly} tone="sky" testid="total-gross-m" />
                    <Total label="Gross Annual" value={preview.totals.gross_annual} tone="sky" testid="total-gross-a" />
                    <Total label="Deductions / Month" value={preview.totals.deductions_monthly} tone="rose" testid="total-ded-m" />
                    <Total label="Net Monthly" value={preview.totals.net_monthly} tone="emerald" testid="total-net-m" />
                    <Total label="Employer / Month" value={preview.totals.employer_monthly} tone="violet" testid="total-er-m" />
                    <Total label="Total CTC / Year" value={preview.totals.ctc_annual} tone="amber" testid="total-ctc-a" />
                </section>
            )}

            {/* Breakdown table */}
            {preview && (() => {
                const nameMap = buildComponentNameMap(components);
                return (
                <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="preview-breakdown">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Seq</th>
                                <th className="text-left px-3 py-2 font-medium">Component</th>
                                <th className="text-left px-3 py-2 font-medium">Nature</th>
                                <th className="text-left px-3 py-2 font-medium">Formula (readable)</th>
                                <th className="text-right px-3 py-2 font-medium">Monthly</th>
                                <th className="text-right px-3 py-2 font-medium">Annual</th>
                                <th className="text-left px-3 py-2 font-medium">GL Code</th>
                                <th className="text-left px-3 py-2 font-medium">Cost Centre</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preview.components.map((c) => (
                                <tr key={c.component_code} className="border-t border-slate-100" data-testid={`breakdown-row-${c.component_code}`}>
                                    <td className="px-3 py-2 text-xs font-mono text-slate-500">{c.sequence_no}</td>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold" data-testid={`breakdown-component-name-${c.component_code}`}>{c.component_name}</div>
                                        <div className="text-[10px] font-mono text-slate-400" title="Stable component code — used internally by the payroll engine">{c.component_code}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${NATURE_TONE[c.nature] || ""}`}>{c.nature.replaceAll("_", " ")}</Badge>
                                    </td>
                                    <td className="px-3 py-2 max-w-[320px]">
                                        <div className="text-xs text-slate-700 truncate" title={formulaTooltip(c.formula, nameMap)} data-testid={`breakdown-formula-pretty-${c.component_code}`}>
                                            {prettyFormula(c.formula, nameMap)}
                                        </div>
                                        {prettyFormula(c.formula, nameMap) !== c.formula && (
                                            <div className="font-mono text-[9px] text-slate-400 truncate" title={c.formula}>
                                                Raw: {c.formula}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{inr(c.monthly_value)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{inr(c.annual_value)}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{c.gl_account_code || "—"}</td>
                                    <td className="px-3 py-2 text-xs">{c.cost_centre || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
                );
            })()}

            {/* History */}
            {history && history.versions.length > 1 && (
                <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="struct-detail-history">
                    <div className="px-5 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <ClockCounterClockwise size={14} /> Version History · {history.total_versions} version{history.total_versions > 1 ? "s" : ""}
                        </h3>
                    </div>
                    <ul className="divide-y divide-slate-100">
                        {history.versions.map((v) => (
                            <li key={v.id} className={`px-5 py-3 flex items-center justify-between ${v.id === structure.id ? "bg-amber-50" : ""}`} data-testid={`history-row-${v.version_no}`}>
                                <div>
                                    <div className="text-sm font-semibold">v{v.version_no} · {formatDate(v.effective_from)}{v.effective_to ? ` → ${formatDate(v.effective_to)}` : " (current)"}</div>
                                    {v.amend_reason && <div className="text-xs text-slate-500 mt-0.5">{v.amend_reason}</div>}
                                </div>
                                <Link to={`/admin/payroll/structures/${v.id}`}>
                                    <Button variant="ghost" size="sm" data-testid={`history-open-${v.version_no}`}>
                                        <CaretRight size={12} />
                                    </Button>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Amend Dialog with side-by-side diff */}
            <Dialog open={openAmend} onOpenChange={setOpenAmend}>
                <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="amend-dialog">
                    <DialogHeader>
                        <DialogTitle>Amend {structure.code} → v{structure.version_no + 1}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">New Effective From</Label>
                            <Input type="date" value={amendForm.effective_from}
                                   onChange={(e) => setAmendForm({ ...amendForm, effective_from: e.target.value })}
                                   data-testid="amend-from" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Amend Reason (audit)</Label>
                            <Input value={amendForm.amend_reason}
                                   onChange={(e) => setAmendForm({ ...amendForm, amend_reason: e.target.value })}
                                   placeholder="e.g. Q4 2025 — raise HRA to 50%"
                                   data-testid="amend-reason" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Components ({amendForm.components.length})</Label>
                        {(() => {
                            const nameMap = buildComponentNameMap(components);
                            return (
                        <div className="space-y-1.5 mt-2" data-testid="amend-rows">
                            {amendForm.components.map((row, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-slate-50 rounded p-2" data-testid={`amend-row-${idx}`}>
                                    <select className="col-span-3 border rounded-md text-xs h-9 px-2" value={row.component_code}
                                            onChange={(e) => {
                                                const next = [...amendForm.components];
                                                next[idx] = { ...next[idx], component_code: e.target.value };
                                                setAmendForm({ ...amendForm, components: next });
                                            }}>
                                        <option value="">— Component —</option>
                                        {components.map((c) => <option key={c.id} value={c.code}>{c.name} ({c.code})</option>)}
                                    </select>
                                    <div className="col-span-7 flex flex-col gap-1">
                                        <Input className="font-mono text-xs" value={row.value_or_formula}
                                               onChange={(e) => {
                                                   const next = [...amendForm.components];
                                                   next[idx] = { ...next[idx], value_or_formula: e.target.value };
                                                   setAmendForm({ ...amendForm, components: next });
                                               }}
                                               data-testid={`amend-row-${idx}-formula`} />
                                        {row.value_or_formula && prettyFormula(row.value_or_formula, nameMap) !== row.value_or_formula && (
                                            <div className="text-[10px] text-slate-600 italic" data-testid={`amend-row-${idx}-formula-pretty`}>
                                                = {prettyFormula(row.value_or_formula, nameMap)}
                                            </div>
                                        )}
                                    </div>
                                    <Input type="number" className="col-span-1" value={row.sequence_no || ""}
                                           onChange={(e) => {
                                               const next = [...amendForm.components];
                                               next[idx] = { ...next[idx], sequence_no: Number(e.target.value) };
                                               setAmendForm({ ...amendForm, components: next });
                                           }} />
                                    <Button variant="ghost" size="sm" className="col-span-1"
                                            onClick={() => setAmendForm({ ...amendForm, components: amendForm.components.filter((_, i) => i !== idx) })}
                                            data-testid={`amend-row-${idx}-del`}>
                                        <X size={12} className="text-rose-600" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                            );
                        })()}
                        <Button variant="outline" size="sm" className="mt-2"
                                onClick={() => setAmendForm({ ...amendForm, components: [...amendForm.components, { component_code: "", value_or_formula: "0", sequence_no: 100 }] })}
                                data-testid="amend-row-add">
                            + Add Component
                        </Button>
                        <div className="text-[10px] text-slate-500 italic mt-2">
                            Tip · Formulas use stable component <b>codes</b> internally (e.g. <span className="font-mono">BASIC</span>). We show the readable name inline so you can review the intent without breaking the engine reference.
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button variant="outline" size="sm" onClick={previewAmend} data-testid="amend-preview-btn">
                            <Sparkle size={12} className="mr-1" /> Compare side-by-side
                        </Button>
                    </div>
                    {amendPreview && (() => {
                        const nameMap = buildComponentNameMap(components);
                        return (
                        <section className="mt-4 bg-amber-50 rounded-xl border border-amber-200 p-3" data-testid="amend-diff">
                            <h4 className="text-xs uppercase tracking-wider text-amber-900 font-semibold mb-2">Side-by-side comparison</h4>
                            <table className="w-full text-xs">
                                <thead className="text-amber-900">
                                    <tr>
                                        <th className="text-left py-1.5">Component</th>
                                        <th className="text-right py-1.5">Current Monthly</th>
                                        <th className="text-right py-1.5">New Monthly</th>
                                        <th className="text-right py-1.5">Δ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {amendPreview.rows.map((r) => {
                                        const cur = (preview?.components || []).find((p) => p.component_code === r.component_code);
                                        const curM = cur ? cur.monthly_value : 0;
                                        const delta = r.monthly - curM;
                                        return (
                                            <tr key={r.component_code} className="border-t border-amber-200" data-testid={`amend-diff-row-${r.component_code}`}>
                                                <td className="py-1.5">
                                                    <div className="font-semibold">{nameMap[r.component_code] || r.component_code}</div>
                                                    <div className="font-mono text-[9px] text-slate-500">{r.component_code}</div>
                                                </td>
                                                <td className="py-1.5 text-right font-mono">{inr(curM)}</td>
                                                <td className="py-1.5 text-right font-mono">{inr(r.monthly)}</td>
                                                <td className={`py-1.5 text-right font-mono ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-slate-500"}`}>
                                                    {delta > 0 ? "+" : ""}{inr(delta)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </section>
                        );
                    })()}
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setOpenAmend(false)} data-testid="amend-cancel">Cancel</Button>
                        <Button onClick={confirmAmend} data-testid="amend-confirm">Confirm Amendment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Total({ label, value, tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
    };
    return (
        <div className={`rounded-xl border p-3 ${map[tone]}`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-wider font-semibold">{label}</div>
            <div className="text-lg font-bold mt-1 font-mono">{inr(value)}</div>
        </div>
    );
}
